import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCreator } from "../creator/CreatorContext";
import { getAsset, getDownloadUrl, NotFoundError } from "../data/client";
import type { PatronAsset, SlotDef } from "../data/types";
import { AssetArt, Button, Display, Panel, PipMeter, Sprite } from "../design/primitives";
import { art, cardSprite } from "../design/sprites";
import { artCandidates } from "../library/coverUrl";
import { ErrorState, LoadingState } from "./states";
import { ReturnRow } from "./ReturnRow";
import {
  collectionFor,
  filtersToSearch,
  onlyGame,
} from "../library/gridState";
import { PdfViewer } from "./PdfViewer";
import { usePreview } from "../preview/PreviewContext";
import css from "./detail.module.css";

type Load =
  | { phase: "loading" }
  | { phase: "missing" }
  | { phase: "error"; error: Error }
  | { phase: "ready"; asset: PatronAsset };

const kb = (bytes: number) =>
  bytes >= 1_000_000
    ? `${(bytes / 1_000_000).toFixed(1)} MB`
    : `${Math.round(bytes / 1000)} KB`;

export default function AssetDetail() {
  const { slug = "" } = useParams();
  const { creator, gameById } = useCreator();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  /*
   * The arrival list — R-9, R-D2.
   *
   * `null` on a deep link, which is what hides the stepper (R-10). Gated on the
   * navigation state rather than on the stored list existing: someone who
   * browsed an hour ago and then opened a shared link in the same tab still has
   * stored data, and their stepper would walk somebody else's filters.
   */
  const arrival = collectionFor(location.state, slug);

  /*
   * Fullscreen lives here, not in the viewer — R-D5.
   *
   * The viewer renders it; this screen owns it, because `Escape` has to mean
   * two different things in sequence (R-14) and only one listener can decide
   * which. Two listeners on one event means one press does both, and which of
   * them counts as "first" depends on the order React attached them.
   */
  const [fullscreen, setFullscreen] = useState(false);

  /*
   * R-5 / R-D3: a **navigation to `/tabs`**, never `history.back()`.
   *
   * Back-by-history works perfectly for everyone testing it, because they
   * arrived from the grid — and dead-ends exactly the readers who arrive by
   * shared link, on an external referrer or a blank tab. The filters ride in
   * the URL already (R-D1), so returning to `/tabs` restores them; scroll comes
   * back from sessionStorage on the other side.
   */
  const back = useCallback(() => navigate("/tabs"), [navigate]);

  /*
   * R-7 and R-14: one listener, two meanings, in order.
   *
   * In fullscreen the first press leaves fullscreen **and stays on the tab**.
   * Only a press with no fullscreen to leave gets out of the tab. Never both at
   * once — a reader pressing Escape to see the chrome again must not find
   * themselves back in the grid.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (fullscreen) {
        setFullscreen(false);
        return;
      }
      back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreen, back]);

  /* R-8: a deliberate new query, not a restoration of the old one. */
  const openGame = (gameId: string) =>
    // Carries `?as=` through, so following the breadcrumb while previewing
    // does not silently drop out of preview (same defect as the search box).
    navigate(`/tabs${filtersToSearch(onlyGame(gameId), searchParams)}`);

  /*
   * R-12: `replace`, so browser back does not have to walk every tab visited by
   * arrow. The state rides along, which is what keeps the stepper alive across
   * a step.
   */
  const step = useCallback(
    (delta: number) => {
      if (!arrival) return;
      const next = arrival.collection[arrival.index + delta];
      if (!next) return;
      navigate(`/tabs/${next}`, { replace: true, state: { fromGrid: true } });
    },
    [arrival, navigate]
  );

  /*
   * R-12: the keyboard. `J`/`K` and the arrow keys.
   *
   * Suppressed while a text input has focus — otherwise typing "jazz" into a
   * search box navigates twice before the reader notices. `Esc` is deliberately
   * **not** here: it belongs with fullscreen, which has to swallow the first
   * press (R-14), and two listeners for one key is exactly the bug R-D5 exists
   * to prevent.
   */
  useEffect(() => {
    if (!arrival) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (e.key === "j" || e.key === "ArrowRight") step(1);
      else if (e.key === "k" || e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [arrival, step]);
  const { previewing } = usePreview();
  const [load, setLoad] = useState<Load>({ phase: "loading" });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoad({ phase: "loading" });
    getAsset(slug)
      .then((asset) => live && setLoad({ phase: "ready", asset }))
      .catch((error: Error) => {
        if (!live) return;
        // A draft and a slug that never existed give the same answer, so the
        // screen must not distinguish them either.
        setLoad(
          error instanceof NotFoundError
            ? { phase: "missing" }
            : { phase: "error", error }
        );
      });
    return () => {
      live = false;
    };
  }, [slug, reloadKey]);

  if (load.phase === "loading") return <LoadingState subLabel="OPENING THE TAB" />;
  if (load.phase === "missing") {
    return (
      <ErrorState
        headline="THERE IS NOTHING AT THIS ADDRESS"
        detail="The link may be old, or the arrangement may not be published yet."
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }
  if (load.phase === "error") {
    return (
      <ErrorState
        code={`ASSET_LOAD_FAILED · ${load.error.name.toUpperCase()}`}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  const { asset } = load;
  const game = gameById(asset.gameId);
  const tint = game?.tint ?? "#8A7FB0";
  const typeDef = creator.assetTypes.find((t) => t.type === asset.assetType);
  const pdf = asset.files.pdf;
  /*
   * The *type* declaring a pdf slot, not this asset happening to have a file
   * in one. A tab whose PDF has not been uploaded yet is still a tab, and the
   * viewer is the right place for it to say the document is not there - the
   * alternative is a screen that silently loses its main column.
   */
  const hasPdfSlot = (typeDef?.slots ?? []).some((slot) => slot.id === "pdf");

  // §7's 2x2 grid. Which four cells appear is decided by the asset type's
  // fields (D-10), not hardcoded to tuning/capo.
  const metaCells: Array<[string, string]> = [
    ...(typeDef?.fields ?? [])
      .filter((f) => f.kind !== "pips")
      .map((f) => [f.label.toUpperCase(), String(asset.fields[f.id] ?? "—")] as [string, string]),
    ...(pdf?.pages ? ([["PAGES", String(pdf.pages)]] as Array<[string, string]>) : []),
  ].slice(0, 4);

  const download = async (slot: SlotDef) => {
    try {
      const url = await getDownloadUrl(asset.id, slot.id);
      window.open(url, "_blank", "noopener");
    } catch {
      // The server is the gate (§11.4). A refusal here is the expected answer
      // for a slot this tier does not hold, not an exception to log loudly.
    }
  };

  return (
    <>
      {/*
        R-1: between the nav and the body, full width. The body keeps its own
        gutter; this row has its own.
      */}
      {/* R-13: chrome hides in fullscreen, this row included. */}
      {!fullscreen && (
      <ReturnRow
        game={game ? { id: game.id, name: game.name } : undefined}
        title={asset.title}
        onBack={back}
        onGame={openGame}
        stepper={
          arrival
            ? {
                position: arrival.index + 1,
                total: arrival.collection.length,
                onPrev: () => step(-1),
                onNext: () => step(1),
              }
            : undefined
        }
      />
      )}
      <div className={css.page}>
      {/*
        PV-8: no viewer at all for a type that has no `pdf` slot - a backing
        track gets the rail and its download, not a chrome bar over a blank
        page claiming a document exists.

        Keyed on the asset id so page and zoom cannot survive into the next
        asset (PV-10). Resetting them by hand is a list someone forgets to add
        to; a new instance has nothing to forget.
      */}
      {hasPdfSlot && (
        <PdfViewer
          key={asset.id}
          assetId={asset.id}
          title={asset.title}
          previewing={previewing}
          expanded={fullscreen}
          onExpandedChange={setFullscreen}
        />
      )}

      <aside className={css.rail}>
        <div className={css.railHead}>
          {/* 68px, and it stays 68px - a cover is a thumbnail everywhere. */}
          <AssetArt
            coverUrls={artCandidates(asset, game, creator.publicAssetsBaseUrl)}
            spriteSrc={cardSprite(typeDef?.cardObject ?? "SHEET", tint)}
            size={68}
          />
          <div>
            <Display size={27} lineHeight={1.12} as="h1">
              {asset.title}
            </Display>
          </div>
        </div>

        <div className={css.difficultyStrip}>
          <span className={css.metaLabel}>DIFFICULTY</span>
          <PipMeter value={Number(asset.fields.difficulty ?? 0)} size={14} showRatio />
        </div>

        {metaCells.length > 0 && (
          <div className={css.metaGrid}>
            {metaCells.map(([label, value]) => (
              <div key={label} className={css.metaCell}>
                <span className={css.metaLabel}>{label}</span>
                <span className={css.metaValue}>{value}</span>
              </div>
            ))}
          </div>
        )}

        <div className={css.downloads}>
          {(typeDef?.slots ?? [])
            .filter((slot) => slot.shortName && asset.files[slot.id])
            .map((slot, index) => {
              const stored = asset.files[slot.id];
              // D-7: each subtype is rendered against its OWN grant. A tier can
              // hold the PDF and not the MusicXML on this same asset.
              if (!asset.grants[slot.id]) {
                return (
                  <a key={slot.id} href="/upgrade" className={css.locked}>
                    <Sprite src={art.lock} size={20} />
                    {slot.label.toUpperCase()} — NOT IN YOUR TIER
                  </a>
                );
              }
              return (
                <Button
                  key={slot.id}
                  variant={index === 0 ? "gold" : "secondary"}
                  size="rail"
                  className={css.download}
                  onClick={() => download(slot)}
                >
                  <Sprite
                    src={slot.shortName === "PDF" ? art.pdf : art.gp}
                    size={20}
                    label={slot.shortName}
                  />
                  DOWNLOAD {slot.shortName?.toUpperCase()}
                  <span className={css.downloadSize}>{kb(stored.size)}</span>
                </Button>
              );
            })}
        </div>

        {/* Only rendered when a URL exists (§25). */}
        {asset.performanceUrl && (
          <a
            className={css.performance}
            href={asset.performanceUrl}
            target="_blank"
            rel="noreferrer noopener"
          >
            <Sprite src={art.crt} size={26} />
            WATCH THE PERFORMANCE
          </a>
        )}

        {asset.tags.length > 0 && (
          <div className={css.tags}>
            {asset.tags.map((id) => {
              const tag = creator.tags.find((t) => t.id === id);
              return tag ? (
                <span key={id} className={css.tag}>
                  {tag.label.toUpperCase()}
                </span>
              ) : null;
            })}
          </div>
        )}

        {asset.note && (
          <Panel className={css.note}>
            <div className={css.noteHead}>
              <span className={css.noteAvatar}>
                {creator.displayName[0]?.toUpperCase()}
              </span>
              {/* Labelled from the creator profile, not the literal string. */}
              <span className={css.noteLabel}>
                NOTE FROM {creator.displayName.split(" ")[0].toUpperCase()}
              </span>
            </div>
            <p className={css.noteBody}>{asset.note}</p>
          </Panel>
        )}
      </aside>
      </div>
    </>
  );
}
